import { CheckmarkOutline16, Edit16 } from '@carbon/icons-react';
import { Tooltip } from 'antd';
import _ from 'lodash';
import moment from 'moment';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import tw from 'tailwind-styled-components';
import { CustomerPortalButtonType } from 'api/customer-portal/theme-api';
import { FormResponseType } from 'api/form';
import { Spotlight } from 'api/spotlight/types';
import { Button } from 'components/atoms/button';
import {
  CpBody00Secondary,
  CpBody01Primary,
  CpBody02Primary,
} from 'components/atoms/customer-portal/typography';
import { StatusIndicator } from 'components/molecules/status-indicator';
import { SpotlightForTasklist } from 'components/organisms/spotlight/spolight-for-task-list';
import { RoutePaths } from 'constant';
import { useSelectedStatusOption } from 'data/fields/hooks';
import { useProjectAsRBACResource } from 'hooks/rbac/useWithAccess';
import { HideIfNotAllowed } from 'hooks/rbac/withAccess';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { useTaskDetailQuery } from 'queries/task';
import { tryGetTextFromHTML } from 'utils/getTextFromHTML';
import { CpButton } from '../../components/cp-button';
import { useGetBackground, useGetStyle } from '../../types/utils';
import {
  CpSectionWrapper,
  CpWidgetDescription,
  CpWidgetTitle,
  PaddingSize,
} from '../common';
import { CpWidgetHeader } from '../common/header';
import { CpTaskCardAssignees } from '../cp-task-card-assignees';
import { CpFormEmptyState } from './empty-state';
import { CpProjectForm, CpProjectFormsWidgetProps } from './types';

const List = styled.div`
  margin-top: var(---cp-widget-description-and-content-margin);
`;

const FormOutlineButton = styled(Button)`
  border-radius: var(--cp-spacing-03);
  border: 2px solid var(--cp-black);
  padding-right: var(--cp-spacing-04);
`;

const ResponseCountButton = styled(Button).attrs({
  className: 'cp-auto-adjust-color',
})`
  background: transparent;
  padding: 0;
  color: var(--cp-text-secondary);
  border-color: transparent;
  box-shadow: none;
`;

const FormSubmitButton = styled(Button)`
  border-radius: 8px;
  background: var(--cp-green-50);
  color: white;
  padding-right: var(--cp-spacing-04);
  border-color: transparent;
`;
const ImageAtLeftWrapper = styled.div<{ isSingleForm }>`
  display: flex;
  gap: var(--cp-spacing-06);
  border: 1px solid var(--cp-border-subtle);
  margin-bottom: var(--cp-spacing-06);
  border-radius: var(--border-radius-small);
  width: ${({ isSingleForm }) => (isSingleForm ? '100%' : 'auto')};
  height: ${({ isSingleForm }) => (isSingleForm ? '486px' : 'auto')};

  @media (min-width: 481px) {
    gap: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 0;
    ${FormOutlineButton},
    ${FormSubmitButton} {
      width: 100%;
      justify-content: center;
    }
  }
`;

const IALImageWrapper = styled.div<{ isSingleForm }>`
  width: ${({ isSingleForm }) => (isSingleForm ? '100%' : '30%')};
  height: ${({ isSingleForm }) => (isSingleForm ? '100%' : 'auto')};
  object-fit: fill;

  @media (min-width: 768px) {
    /* max-width: 50%;
    width: 40%; */
  }
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const IALImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  @media (min-width: 768px) {
    width: 100%;
    min-width: 100%;
  }
`;

const IALContent = styled.div<{ isSpotlightEnabled; isSingleForm }>`
  display: flex;
  flex-direction: column;
  padding: var(--cp-spacing-07);
  gap: var(--cp-spacing-05);
  background: ${({ isSpotlightEnabled }) =>
    isSpotlightEnabled ? '#FFF5EB' : 'var(--cp-white)'};
  justify-content: ${({ isSingleForm }) => (isSingleForm ? 'center' : 'start')};

  @media (min-width: 481px) {
    width: 70%;
  }
  @media (max-width: 480px) {
    padding: var(--cp-spacing-06) var(--cp-spacing-05);
  }
`;

const DateAndAssignee = styled.div`
  display: flex;
  align-items: center;
  gap: var(--cp-spacing-05);
`;

const FormName = styled(CpBody02Primary)``;

const TitleAndDescription = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-03);
`;

const AssigneeStatusAndDateWrapper = styled.div<{
  breakIntoMultipleLines?: boolean;
}>`
  display: flex;
  flex-direction: row;
  gap: var(--cp-spacing-05);

  > ${CpBody00Secondary} {
    display: flex;
    align-items: center;
    justify-content: start;
  }
  ${({ breakIntoMultipleLines }) =>
    breakIntoMultipleLines
      ? css`
          @media (max-width: 480px) {
            display: grid;
            grid-template-columns: 1fr 1fr;
            width: 100%;
            row-gap: var(--cp-spacing-02);
            ${CpBody00Secondary} {
              display: flex;
            }
            > * {
              justify-content: flex-start;
            }
          }
        `
      : ''};
`;
const MFWIATLActionsWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-05);

  @media (max-width: 480px) {
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: var(--cp-spacing-05);
  }
`;

const SingleFormImageWrapper = styled.div`
  width: 50%;
  height: 100%;
`;

const SingleFormContentWrapper = styled.div<{ isSpotlightEnabled }>`
  width: 50%;
  height: 100%;
  display: flex;
  align-items: center;
  background: ${({ isSpotlightEnabled }) =>
    isSpotlightEnabled ? '#FFF5EB' : 'var(--cp-white)'};
`;

interface FormComponentProps {
  isSingleForm: boolean;
  form: CpProjectForm;
  formDescriptionText: string;
  formattedDueDate: string;
  onSubmissionClick: (form: CpProjectForm) => void;
  projectId: number;
  spotlight: Spotlight;
}

const FormComponent: React.FC<FormComponentProps> = ({
  isSingleForm,
  form,
  formDescriptionText,
  formattedDueDate,
  onSubmissionClick,
  projectId,
  spotlight,
}) => {
  const { data: task } = useTaskDetailQuery(
    ['task_id', projectId ?? -1, form.task?.taskId ?? -1],
    {
      enabled:
        !!(form?.task?.taskId && projectId) &&
        form?.task?.taskId !== -1 &&
        projectId !== -1,
    },
  );
  const projectResource = useProjectAsRBACResource(projectId);
  const formResponseCount = form?.responseCount ?? 0;
  const assignee = task?.assignee;
  const isEmptyAssignee = useMemo(
    () =>
      (assignee?.teams ?? []).length === 0 &&
      (assignee?.users ?? []).length === 0,
    [assignee?.teams, assignee?.users],
  );
  const isMultiResponseMode =
    form?.formTemplate?.formSettings?.formResponseType ===
      FormResponseType.MultipleResponse ?? false;
  const redirectToFormResponses = () => {
    const formResponseUrl = RoutePaths.projectFormResponseByInstance
      .replace(':projectId', projectId.toString())
      .replace(':instanceId', (task?.formInstanceId ?? '').toString());

    window.open(formResponseUrl, '_blank');
  };

  return (
    <ImageAtLeftWrapper key={form.task?.taskId} isSingleForm={isSingleForm}>
      {isSingleForm ? (
        <>
          <SingleFormImageWrapper>
            <IALImage
              src={
                form.formTemplate?.formTheme?.headerImage ??
                'https://s3.us-east-1.amazonaws.com/rocketlane-profile-pic-public/657/45b397c4-1d90-49af-884e-12f1788289d8/Form%20Header-12.png'
              }
              alt={form.formTemplate?.name}
            />
          </SingleFormImageWrapper>
          <SingleFormContentWrapper isSpotlightEnabled={!!spotlight}>
            <IALContent
              isSpotlightEnabled={!!spotlight}
              isSingleForm={isSingleForm}
            >
              {!!spotlight && (
                <Tooltip title={spotlight.caption}>
                  <SpotlightForTasklist spotlight={spotlight} />
                </Tooltip>
              )}
              <TitleAndDescription>
                <FormName>{form.task?.taskName}</FormName>
                {formDescriptionText != null && formDescriptionText !== '' ? (
                  <CpBody01Primary>{formDescriptionText}</CpBody01Primary>
                ) : (
                  <></>
                )}
              </TitleAndDescription>
              {formattedDueDate ||
              !isEmptyAssignee ||
              (isMultiResponseMode && formResponseCount > 0) ? (
                <DateAndAssignee>
                  {!isEmptyAssignee ? (
                    <CpTaskCardAssignees assignee={assignee} />
                  ) : (
                    <></>
                  )}
                  {formattedDueDate ? (
                    <CpBody00Secondary>{formattedDueDate}</CpBody00Secondary>
                  ) : (
                    <></>
                  )}
                  {isMultiResponseMode && formResponseCount > 0 ? (
                    <ResponseCountButton
                      size="small"
                      onClick={redirectToFormResponses}
                    >
                      <FormattedMessage
                        id="cp.show_responses"
                        values={{
                          count: formResponseCount,
                        }}
                      />
                    </ResponseCountButton>
                  ) : (
                    <></>
                  )}
                </DateAndAssignee>
              ) : (
                <></>
              )}
              <MFWIATLActionsWrapper>
                {form?.hasAnswerBeenSubmitted && !isMultiResponseMode ? (
                  <>
                    <FormSubmitButton
                      icon={<CheckmarkOutline16 />}
                      iconPlacement="left"
                    >
                      <FormattedMessage id="submitted" />
                    </FormSubmitButton>
                    <HideIfNotAllowed
                      ignoreForBotUsers
                      rule="ALL_FORM_RESPONSES:WRITE"
                      resourceData={projectResource}
                    >
                      <FormOutlineButton
                        onClick={() => onSubmissionClick(form)}
                        icon={<Edit16 />}
                        iconPlacement="left"
                      >
                        <FormattedMessage id="projects.form_responses.edit" />
                      </FormOutlineButton>
                    </HideIfNotAllowed>
                  </>
                ) : (
                  <HideIfNotAllowed
                    ignoreForBotUsers
                    rule="ALL_FORM_RESPONSES:WRITE"
                    resourceData={projectResource}
                  >
                    <FormOutlineButton
                      onClick={() => onSubmissionClick(form)}
                      icon={<Edit16 />}
                      iconPlacement="left"
                    >
                      {form?.formTemplate?.formTheme?.formCta ?? (
                        <FormattedMessage id="cp.submit_form" />
                      )}
                    </FormOutlineButton>
                  </HideIfNotAllowed>
                )}
              </MFWIATLActionsWrapper>
            </IALContent>
          </SingleFormContentWrapper>
        </>
      ) : (
        <>
          <IALImageWrapper isSingleForm={isSingleForm}>
            <IALImage
              src={
                form.formTemplate?.formTheme?.headerImage ??
                'https://s3.us-east-1.amazonaws.com/rocketlane-profile-pic-public/657/45b397c4-1d90-49af-884e-12f1788289d8/Form%20Header-12.png'
              }
              alt={form.formTemplate?.name}
            />
          </IALImageWrapper>
          <IALContent
            isSpotlightEnabled={!!spotlight}
            isSingleForm={isSingleForm}
          >
            {!!spotlight && (
              <Tooltip title={spotlight.caption}>
                <SpotlightForTasklist spotlight={spotlight} />
              </Tooltip>
            )}
            <TitleAndDescription>
              <FormName>{form.task?.taskName}</FormName>
              {formDescriptionText != null && formDescriptionText !== '' ? (
                <CpBody01Primary>{formDescriptionText}</CpBody01Primary>
              ) : (
                <></>
              )}
            </TitleAndDescription>
            {formattedDueDate ||
            !isEmptyAssignee ||
            (isMultiResponseMode && formResponseCount > 0) ? (
              <DateAndAssignee>
                {!isEmptyAssignee ? (
                  <CpTaskCardAssignees assignee={assignee} />
                ) : (
                  <></>
                )}
                {formattedDueDate ? (
                  <CpBody00Secondary>{formattedDueDate}</CpBody00Secondary>
                ) : (
                  <></>
                )}
                {isMultiResponseMode && formResponseCount > 0 ? (
                  <ResponseCountButton
                    size="small"
                    onClick={redirectToFormResponses}
                  >
                    <FormattedMessage
                      id="cp.show_responses"
                      values={{
                        count: formResponseCount,
                      }}
                    />
                  </ResponseCountButton>
                ) : (
                  <></>
                )}
              </DateAndAssignee>
            ) : (
              <></>
            )}
            <MFWIATLActionsWrapper>
              {form?.hasAnswerBeenSubmitted && !isMultiResponseMode ? (
                <>
                  <FormSubmitButton
                    icon={<CheckmarkOutline16 />}
                    iconPlacement="left"
                  >
                    <FormattedMessage id="submitted" />
                  </FormSubmitButton>
                  <HideIfNotAllowed
                    ignoreForBotUsers
                    rule="ALL_FORM_RESPONSES:WRITE"
                    resourceData={projectResource}
                  >
                    <FormOutlineButton
                      onClick={() => onSubmissionClick(form)}
                      icon={<Edit16 />}
                      iconPlacement="left"
                    >
                      <FormattedMessage id="projects.form_responses.edit" />
                    </FormOutlineButton>
                  </HideIfNotAllowed>
                </>
              ) : (
                <HideIfNotAllowed
                  ignoreForBotUsers
                  rule="ALL_FORM_RESPONSES:WRITE"
                  resourceData={projectResource}
                >
                  <FormOutlineButton
                    onClick={() => onSubmissionClick(form)}
                    icon={<Edit16 />}
                    iconPlacement="left"
                  >
                    {form?.formTemplate?.formTheme?.formCta ?? (
                      <FormattedMessage id="cp.submit_form" />
                    )}
                  </FormOutlineButton>
                </HideIfNotAllowed>
              )}
            </MFWIATLActionsWrapper>
          </IALContent>
        </>
      )}
    </ImageAtLeftWrapper>
  );
};

export const MultiFormWithImageAtTheLeft: React.FC<CpProjectFormsWidgetProps> =
  ({ data, disabled, widget, projectId }) => {
    const bg = useGetBackground(widget);

    const spotlightMap = useMemo(() => {
      return _.keyBy(data?.spotlights ?? [], 'resourceId');
    }, [data?.spotlights]);

    const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

    const onSubmissionClick = useCallback(
      (form: CpProjectForm) => {
        openTaskDrawer(projectId, form.task.taskId);
      },
      [openTaskDrawer, projectId],
    );

    const paddingY = useGetStyle(widget, 'paddingY', PaddingSize.Large);

    return (
      <CpSectionWrapper
        background={bg}
        paddingY={paddingY}
        isDisabled={disabled}
      >
        <CpWidgetHeader title={data.title} description={data?.description} />

        <List>
          {data.forms.length === 0 ? (
            <CpFormEmptyState />
          ) : (
            data.forms.map((form) => {
              const formattedDueDate = form?.task?.dueDate
                ? moment(form.task.dueDate).format('MMM DD, YYYY')
                : '';

              const formDescriptionText = tryGetTextFromHTML(
                form?.task?.taskDescription,
              );

              return (
                <FormComponent
                  spotlight={spotlightMap[form.task?.taskId]}
                  isSingleForm={false}
                  form={form}
                  formDescriptionText={formDescriptionText}
                  formattedDueDate={formattedDueDate}
                  onSubmissionClick={onSubmissionClick}
                  projectId={projectId}
                />
              );
            })
          )}
        </List>
      </CpSectionWrapper>
    );
  };

const MultiFormWithAllRoundBorderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cp-spacing-06);
  border: 1px solid var(--cp-border-subtle-auto-adjust);
  border-radius: var(--border-radius-small);
  margin-bottom: var(--cp-spacing-06);
  /* background-color: var(--cp-white); */
  border-radius: var(--border-radius-small);

  @media (max-width: 480px) {
    flex-direction: column;
  }
`;

const MFWARBContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 70%;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

const MFWARBActionsWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-05);

  @media (min-width: 768px) {
    width: 100%;
    justify-content: flex-end;
  }

  @media (max-width: 480px) {
    width: 100%;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: var(--cp-spacing-05);
  }
`;

const StatusWrapper = tw.div`
  rl-cp-project-plan-task-status
  cp-h-full
  cp-inline-flex
  cp-items-center
  cp-justify-center
  cp-mb-0
  `;

const StatusLabel = styled(CpBody00Secondary)`
  margin-bottom: 0;
  margin-left: var(--cp-spacing-02);
`;

export const MultiFormWithAllRoundBorder: React.FC<CpProjectFormsWidgetProps> =
  ({ data, disabled, widget, projectId }) => {
    const bg = useGetBackground(widget);
    const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

    const onSubmissionClick = useCallback(
      (form: CpProjectForm) => {
        openTaskDrawer(projectId, form.task.taskId);
      },
      [openTaskDrawer, projectId],
    );

    return (
      <CpSectionWrapper background={bg} isDisabled={disabled}>
        <CpWidgetTitle autoAdjustColor>{data.title}</CpWidgetTitle>
        {data?.description ? (
          <CpWidgetDescription autoAdjustColor>
            {data.description}
          </CpWidgetDescription>
        ) : null}
        <List>
          {data.forms.map((form) => {
            const formattedDueDate = form?.task?.dueDate
              ? moment(form.task.dueDate).format('MMM DD, YYYY')
              : '';

            return (
              <MultiFormWithAllRoundBorderWrapper key={form.task?.taskId}>
                <MFWARBContentWrapper>
                  <DateAndAssignee>
                    {formattedDueDate ? (
                      <CpBody00Secondary>{formattedDueDate}</CpBody00Secondary>
                    ) : (
                      <></>
                    )}
                  </DateAndAssignee>
                  <FormName>{form.task?.taskName}</FormName>
                  <CpBody01Primary>
                    {form.formTemplate?.description}
                  </CpBody01Primary>
                </MFWARBContentWrapper>
                <MFWARBActionsWrapper>
                  <CpButton
                    onClick={() => onSubmissionClick(form)}
                    cpType={CustomerPortalButtonType.Primary}
                    icon={
                      !form?.hasAnswerBeenSubmitted ? (
                        <Edit16 />
                      ) : (
                        <CheckmarkOutline16 />
                      )
                    }
                    iconPlacement="left"
                  >
                    {form?.hasAnswerBeenSubmitted ? (
                      <FormattedMessage id="projects.form_responses.edit" />
                    ) : (
                      form?.formTemplate?.formTheme?.formCta ?? (
                        <FormattedMessage id="cp.submit_form" />
                      )
                    )}
                  </CpButton>
                </MFWARBActionsWrapper>
              </MultiFormWithAllRoundBorderWrapper>
            );
          })}
        </List>
      </CpSectionWrapper>
    );
  };

const MultiFormWithBottomBorderImage = styled.img`
  width: 74px;
  height: 74px;
  object-fit: cover;
  @media (max-width: 480px) {
    display: none;
  }
`;
const MultiFormWithBottomBorderWrapper = styled.div<{
  isSpotLightEnabled: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--cp-spacing-06);
  border-bottom: 1px solid var(--cp-border-subtle-auto-adjust);
  ${FormOutlineButton} {
    background: transparent;
  }
  ${({ isSpotLightEnabled }) =>
    !isSpotLightEnabled
      ? css`
          background: transparent;
          ${FormOutlineButton} {
            .cp-dark-background & {
              border-color: var(--cp-white);
              color: var(--cp-white);
            }
          }
        `
      : css`
          background: #fff5eb;
        `};

  @media (max-width: 768px) {
    flex-direction: column;
    padding-left: var(--cp-spacing-05);
    padding-right: var(--cp-spacing-05);
  }
`;

const MFWBBMainContentRowWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-03);
`;
const MFWBBMainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--cp-spacing-03);
  width: 100%;
`;

const MFWBBContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 70%;
  gap: var(--cp-spacing-03);

  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const MFWBBActionsWrapper = styled.div`
  display: flex;
  gap: var(--cp-spacing-05);
  width: 100%;
  justify-content: flex-end;
  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: var(--cp-spacing-05);
  }

  @media (max-width: 480px) {
    align-items: center;
    justify-content: center;
    flex-direction: column;
    margin-top: var(--cp-spacing-05);
    gap: var(--cp-spacing-03);
    ${FormOutlineButton},
    ${FormSubmitButton} {
      width: 100%;
      justify-content: center;
    }
  }
`;

const MultiFormWithBottomBorderWrapperComponent = ({
  form,
  formDescriptionText,
  formattedDueDate,
  onSubmissionClick,
  spotlight,
  projectId,
}: {
  form: CpProjectForm;
  formDescriptionText: string;
  formattedDueDate: string;
  onSubmissionClick: (form: CpProjectForm) => void;
  spotlight: Spotlight;
  projectId: number;
}) => {
  const { task } = form;

  const assignee = task?.assignee;
  const formResponseCount = form?.responseCount ?? 0;

  const fields = useMemo(() => task?.fields, [task?.fields]);
  const selectedStatusOption = useSelectedStatusOption(fields || []);
  const isEmptyAssignee = useMemo(
    () =>
      (assignee?.teams ?? []).length === 0 &&
      (assignee?.users ?? []).length === 0,
    [assignee?.teams, assignee?.users],
  );

  const projectResource = useProjectAsRBACResource(projectId);
  const isMultiResponseMode =
    form?.formTemplate?.formSettings?.formResponseType ===
      FormResponseType.MultipleResponse ?? false;
  const redirectToFormResponses = () => {
    const formResponseUrl = RoutePaths.projectFormResponseByInstance
      .replace(':projectId', projectId.toString())
      .replace(':instanceId', (task?.formInstanceId ?? '').toString());

    window.open(formResponseUrl, '_blank');
  };
  const hasAllFourInfomations = useMemo(
    () =>
      !!(
        formattedDueDate &&
        !isEmptyAssignee &&
        selectedStatusOption != null &&
        isMultiResponseMode &&
        formResponseCount > 0
      ),
    [
      formResponseCount,
      formattedDueDate,
      isEmptyAssignee,
      isMultiResponseMode,
      selectedStatusOption,
    ],
  );

  return (
    <MultiFormWithBottomBorderWrapper
      isSpotLightEnabled={false}
      key={form.task?.taskId}
      data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}`}
    >
      <MFWBBContentWrapper>
        {!!spotlight && (
          <Tooltip title={spotlight.caption}>
            <SpotlightForTasklist
              data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.spotlight`}
              spotlight={spotlight}
            />
          </Tooltip>
        )}
        <MFWBBMainContentRowWrapper>
          <MultiFormWithBottomBorderImage
            src={
              form.formTemplate?.formTheme?.headerImage ??
              'https://s3.us-east-1.amazonaws.com/rocketlane-profile-pic-public/657/45b397c4-1d90-49af-884e-12f1788289d8/Form%20Header-12.png'
            }
            alt={form.formTemplate?.name}
          />
          <MFWBBMainContentWrapper>
            <TitleAndDescription>
              <FormName
                data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.form-name`}
                autoAdjustColor
              >
                {form.task?.taskName}
              </FormName>
              {formDescriptionText != null && formDescriptionText !== '' ? (
                <CpBody01Primary
                  data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.form-description`}
                  autoAdjustColor
                >
                  {formDescriptionText}
                </CpBody01Primary>
              ) : (
                <></>
              )}
            </TitleAndDescription>
            {formattedDueDate ||
            !isEmptyAssignee ||
            selectedStatusOption != null ||
            (isMultiResponseMode && formResponseCount > 0) ? (
              <AssigneeStatusAndDateWrapper
                breakIntoMultipleLines={hasAllFourInfomations}
                data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.assignee-status-and-date`}
              >
                {formattedDueDate ? (
                  <CpBody00Secondary
                    data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.due-date`}
                    autoAdjustColor
                  >
                    {formattedDueDate}
                  </CpBody00Secondary>
                ) : (
                  <></>
                )}
                {!isEmptyAssignee ? (
                  <CpTaskCardAssignees autoAdjustColor assignee={assignee} />
                ) : (
                  <></>
                )}
                {selectedStatusOption != null ? (
                  <StatusWrapper
                    data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.status`}
                  >
                    {selectedStatusOption ? (
                      <StatusIndicator status={selectedStatusOption} />
                    ) : (
                      <></>
                    )}
                    <StatusLabel
                      data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.status-label`}
                      autoAdjustColor
                    >
                      {selectedStatusOption?.label}
                    </StatusLabel>
                  </StatusWrapper>
                ) : (
                  <></>
                )}
                {isMultiResponseMode && formResponseCount > 0 ? (
                  <ResponseCountButton
                    size="small"
                    onClick={redirectToFormResponses}
                  >
                    <FormattedMessage
                      id="cp.show_responses"
                      values={{
                        count: formResponseCount,
                      }}
                    />
                  </ResponseCountButton>
                ) : (
                  <></>
                )}
              </AssigneeStatusAndDateWrapper>
            ) : (
              <></>
            )}
          </MFWBBMainContentWrapper>
        </MFWBBMainContentRowWrapper>
      </MFWBBContentWrapper>
      <MFWBBActionsWrapper>
        {/* <CpButton
          onClick={() => onSubmissionClick(form)}
          cpType={CustomerPortalButtonType.Primary}
          icon={
            !form?.hasAnswerBeenSubmitted ? <Edit16 /> : <CheckmarkOutline16 />
          }
          iconPlacement="left"
        >
          {form?.hasAnswerBeenSubmitted ? (
            <FormattedMessage id="projects.form_responses.edit" />
          ) : (
            form?.formTemplate?.formTheme?.formCta ?? (
              <FormattedMessage id="cp.submit_form" />
            )
          )}
        </CpButton> */}

        {form?.hasAnswerBeenSubmitted && !isMultiResponseMode ? (
          <>
            <FormSubmitButton
              data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.submitted`}
              icon={<CheckmarkOutline16 />}
              iconPlacement="left"
            >
              <FormattedMessage id="submitted" />
            </FormSubmitButton>
            <HideIfNotAllowed
              ignoreForBotUsers
              rule="ALL_FORM_RESPONSES:WRITE"
              resourceData={projectResource}
            >
              <FormOutlineButton
                data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.edit`}
                onClick={() => onSubmissionClick(form)}
                icon={<Edit16 />}
                iconPlacement="left"
              >
                <FormattedMessage id="projects.form_responses.edit" />
              </FormOutlineButton>
            </HideIfNotAllowed>
          </>
        ) : (
          <HideIfNotAllowed
            ignoreForBotUsers
            rule="ALL_FORM_RESPONSES:WRITE"
            resourceData={projectResource}
          >
            <FormOutlineButton
              data-cy={`cp.multi-form-with-bottom-border.${form?.task?.taskName}.submit`}
              onClick={() => onSubmissionClick(form)}
              icon={<Edit16 />}
              iconPlacement="left"
            >
              {form?.formTemplate?.formTheme?.formCta ?? (
                <FormattedMessage id="cp.submit_form" />
              )}
            </FormOutlineButton>
          </HideIfNotAllowed>
        )}
      </MFWBBActionsWrapper>
    </MultiFormWithBottomBorderWrapper>
  );
};

export const MultiFormWithBottomBorder: React.FC<CpProjectFormsWidgetProps> = ({
  data,
  disabled,
  widget,
  projectId,
}) => {
  const bg = useGetBackground(widget);
  const paddingY = useGetStyle(widget, 'paddingY', PaddingSize.Medium);
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();

  const spotlightMap = useMemo(() => {
    return _.keyBy(data?.spotlights ?? [], 'resourceId');
  }, [data?.spotlights]);

  const onSubmissionClick = useCallback(
    (form: CpProjectForm) => {
      openTaskDrawer(projectId, form.task.taskId);
    },
    [openTaskDrawer, projectId],
  );

  return (
    <CpSectionWrapper background={bg} paddingY={paddingY} isDisabled={disabled}>
      <CpWidgetHeader>
        <CpWidgetHeader.Title
          data-cy="cp.multi-form-with-bottom-border-title"
          autoAdjustColor
        >
          {data.title}
        </CpWidgetHeader.Title>
        {data?.description ? (
          <CpWidgetHeader.Description
            autoAdjustColor
            data-cy="cp.multi-form-with-bottom-border-description"
          >
            {data.description}
          </CpWidgetHeader.Description>
        ) : null}
      </CpWidgetHeader>
      <List>
        {data.forms.length === 0 ? (
          <CpFormEmptyState />
        ) : (
          data.forms.map((form) => {
            const formattedDueDate = form?.task?.dueDate
              ? moment(form.task.dueDate).format('MMM DD, YYYY')
              : '';

            const formDescriptionText = tryGetTextFromHTML(
              form?.task?.taskDescription,
            );

            return (
              <MultiFormWithBottomBorderWrapperComponent
                form={form}
                formDescriptionText={formDescriptionText}
                formattedDueDate={formattedDueDate}
                onSubmissionClick={onSubmissionClick}
                spotlight={spotlightMap[form.task?.taskId]}
                projectId={projectId}
              />
            );
          })
        )}
      </List>
    </CpSectionWrapper>
  );
};
